import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import { useToast } from '../../contexts/ToastContext';
import { testWebhook } from '../../controllers/webhook';
import { getWebhookJoin } from '../../controllers/incubations';
import config from '../../config';
import './WebhookInstructionsScreen.css';

const WebhookInstructionsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showLoading, dismiss } = useToast();
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [bodyFormat, setBodyFormat] = useState('standard'); // 'standard' | 'zapier'

  // Get incubation data from navigation state
  const incubation = location.state?.incubation;

  // Build full endpoint from config
  const endpointPathStandard = 'api/webhooks/add_new_participant';
  const endpointPathZapier   = 'api/webhooks/add_new_participant_zapier';
  const joinUrl = (base, path) => {
    if (!base) return `<BASE_URL>/${path.replace(/^\/+/, '')}`; // placeholder when not configured
    const normalizedBase = String(base).replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    return `${normalizedBase}/${normalizedPath}`;
  };
  const fullEndpoint = useMemo(() => {
    const path = bodyFormat === 'zapier' ? endpointPathZapier : endpointPathStandard;
    return joinUrl(config.base_url, path);
  }, [config?.base_url, bodyFormat]);

  // Helpers to generate sample body
  const mapFieldTypeToText = (type) => {
    const t = String(type || '').toLowerCase();
    if (t === 'number' || t === 'integer' || t === 'float') return 'Number';
    if (t === 'boolean') return 'Boolean';
    if (t === 'date' || t === 'datetime') return t === 'datetime' ? 'DateTime' : 'Date';
    if (t === 'auto generated') return 'Auto Generated';
    // choices, text, string, unknown
    return 'String';
  };

  const formatCriteriaKey = (name) => {
    if (!name) return '';
    const base = String(name).trim().toLowerCase().replace(/\s+/g, '_');
    // handle multi-char operators first, then single
    const withOps = base
      .replace(/>=/g, '_>=_')
      .replace(/<=/g, '_<=_')
      .replace(/!=/g, '_!=_')
      .replace(/>/g, '_>_')
      .replace(/</g, '_<_')
      .replace(/=/g, '_=_');
    return withOps
      // preserve operator characters > < = !
      .replace(/[^a-z0-9_><=!]/g, '_')
      .replace(/__+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const sampleRequestBodyDisplay = useMemo(() => {
    const queryObj = {};
    const dataObj = {};

    if (incubation && Array.isArray(incubation.data_fields)) {
      incubation.data_fields.forEach((field) => {
        if (!field || !field.name) return;
        // query keys as optional markers with type
        queryObj[field.name] = mapFieldTypeToText(field.type) + ' (Optional)';
        // data keys with expected type
        dataObj[field.name] = mapFieldTypeToText(field.type);
      });
    }

    if (incubation && Array.isArray(incubation.inclusion_criteria)) {
      incubation.inclusion_criteria.forEach((criteria) => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria?.name;
        if (!criteriaName) return;
        const key = formatCriteriaKey(criteriaName);
        if (key) {
          dataObj[key] = 'Boolean';
        }
      });
    }

    if (incubation && Array.isArray(incubation.exclusion_criteria)) {
      incubation.exclusion_criteria.forEach((criteria) => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria?.name;
        if (!criteriaName) return;
        const key = formatCriteriaKey(criteriaName);
        if (key) {
          dataObj[key] = 'Boolean';
        }
      });
    }

    const TypeToken = ({ text }) => (
      <span style={{ color: 'maroon' }}>{text}</span>
    );

    const lines = [];
    lines.push('{');
    lines.push(
      <React.Fragment key="line-incubation_key">
        {'  "incubation_key": '}<TypeToken text="String" />{' (Required),'}
      </React.Fragment>
    );
    lines.push(
      <React.Fragment key="line-access_key">
        {'  "access_key": '}<TypeToken text="String" />{' (Required),'}
      </React.Fragment>
    );
    lines.push('  "query": {');
    const queryKeys = Object.keys(queryObj);
    if (queryKeys.length === 0) {
      lines.push('    // Add optional query keys from incubation.data_fields');
    } else {
      queryKeys.forEach((k, idx) => {
        const isLast = idx === queryKeys.length - 1;
        const typeAndOptional = queryObj[k].split(' ');
        const type = typeAndOptional[0];
        const optional = typeAndOptional.slice(1).join(' ');
        lines.push(
          <React.Fragment key={`query-line-${k}`}>
            {`    "${k}": `}
            <TypeToken text={type} />
            {` ${optional}${isLast ? '' : ','}`}
          </React.Fragment>
        );
      });
    }
    lines.push('  },');
    lines.push('  "data": {');
    const dataKeys = Object.keys(dataObj);
    if (dataKeys.length === 0) {
      lines.push('    // Add data fields with expected types and criteria booleans');
    } else {
      dataKeys.forEach((k, idx) => {
        const isLast = idx === dataKeys.length - 1;
        const token = <TypeToken key={`type-${k}`} text={dataObj[k]} />;
        lines.push(
          <React.Fragment key={`line-${k}`}>
            {`    "${k}": `}
            {token}
            {isLast ? '' : ','}
          </React.Fragment>
        );
      });
    }
    lines.push('  }');
    lines.push('}');

    return (
      <code>
        {lines.map((line, idx) => (
          <React.Fragment key={`l-${idx}`}>
            {line}
            {'\n'}
          </React.Fragment>
        ))}
      </code>
    );
  }, [incubation]);

  const sampleRequestBodyCopy = useMemo(() => {
    const queryObj = {};
    const dataObj = {};

    if (incubation && Array.isArray(incubation.data_fields)) {
      incubation.data_fields.forEach((field) => {
        if (!field || !field.name) return;
        queryObj[field.name] = mapFieldTypeToText(field.type) + ' (Optional)';
        dataObj[field.name] = mapFieldTypeToText(field.type);
      });
    }

    if (incubation && Array.isArray(incubation.inclusion_criteria)) {
      incubation.inclusion_criteria.forEach((criteria) => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria?.name;
        if (!criteriaName) return;
        const key = formatCriteriaKey(criteriaName);
        if (key) {
          dataObj[key] = 'Boolean';
        }
      });
    }

    if (incubation && Array.isArray(incubation.exclusion_criteria)) {
      incubation.exclusion_criteria.forEach((criteria) => {
        const criteriaName = typeof criteria === 'string' ? criteria : criteria?.name;
        if (!criteriaName) return;
        const key = formatCriteriaKey(criteriaName);
        if (key) {
          dataObj[key] = 'Boolean';
        }
      });
    }

    const indent = (n) => '  '.repeat(n);
    const lines = [];
    lines.push('{');
    lines.push(`${indent(1)}"incubation_key": String (Required),`);
    lines.push(`${indent(1)}"access_key": String (Required),`);
    lines.push(`${indent(1)}"query": {`);
    const queryKeys = Object.keys(queryObj);
    if (queryKeys.length === 0) {
      lines.push(`${indent(2)}// Add optional query keys from incubation.data_fields`);
    } else {
      queryKeys.forEach((k, idx) => {
        const isLast = idx === queryKeys.length - 1;
        lines.push(`${indent(2)}"${k}": ${queryObj[k]}${isLast ? '' : ','}`)
      });
    }
    lines.push(`${indent(1)}},`);
    lines.push(`${indent(1)}"data": {`);
    const dataKeys = Object.keys(dataObj);
    if (dataKeys.length === 0) {
      lines.push(`${indent(2)}// Add data fields with expected types and criteria booleans`);
    } else {
      dataKeys.forEach((k, idx) => {
        const isLast = idx === dataKeys.length - 1;
        lines.push(`${indent(2)}"${k}": ${dataObj[k]}${isLast ? '' : ','}`);
      });
    }
    lines.push(`${indent(1)}}`);
    lines.push('}');
    return lines.join('\n');
  }, [incubation]);

  const sampleResponseBody = useMemo(() => {
    return JSON.stringify({ success: true, message: 'Webhook reached successfully.' }, null, 2);
  }, []);

  // Shared collector for flat (Zapier) keys
  const collectFlatKeys = (inc) => {
    const dataKeys = [];
    const criteriaKeys = [];

    if (inc && Array.isArray(inc.data_fields)) {
      inc.data_fields.forEach((field) => {
        if (!field || !field.name) return;
        dataKeys.push({ key: field.name, type: mapFieldTypeToText(field.type) });
      });
    }
    const pushCriteria = (list) => {
      if (!Array.isArray(list)) return;
      list.forEach((c) => {
        const name = typeof c === 'string' ? c : c?.name;
        if (!name) return;
        const key = formatCriteriaKey(name);
        if (key) criteriaKeys.push(key);
      });
    };
    pushCriteria(inc?.inclusion_criteria);
    pushCriteria(inc?.exclusion_criteria);

    return { dataKeys, criteriaKeys };
  };

  const sampleFlatBodyDisplay = useMemo(() => {
    const TypeToken = ({ text }) => (
      <span style={{ color: 'maroon' }}>{text}</span>
    );

    const { dataKeys, criteriaKeys } = collectFlatKeys(incubation);

    const rows = [
      { k: 'incubation_key', type: 'String', suffix: ' (Required)' },
      { k: 'access_key',     type: 'String', suffix: ' (Required)' },
      ...dataKeys.map(({ key, type }) => ({ k: `data_${key}`, type, suffix: '' })),
      ...criteriaKeys.map((key) => ({ k: `data_${key}`, type: 'Boolean', suffix: '' })),
    ];

    const lines = [];
    lines.push('{');
    if (rows.length === 2) {
      lines.push('  // Add data_<field_name> entries for each data field or criterion');
    }
    rows.forEach((row, idx) => {
      const isLast = idx === rows.length - 1;
      lines.push(
        <React.Fragment key={`flat-line-${row.k}-${idx}`}>
          {`  "${row.k}": `}
          <TypeToken text={row.type} />
          {row.suffix}
          {isLast ? '' : ','}
        </React.Fragment>
      );
    });
    lines.push('}');

    return (
      <code>
        {lines.map((line, idx) => (
          <React.Fragment key={`fl-${idx}`}>
            {line}
            {'\n'}
          </React.Fragment>
        ))}
      </code>
    );
  }, [incubation]);

  const sampleFlatBodyCopy = useMemo(() => {
    const { dataKeys, criteriaKeys } = collectFlatKeys(incubation);

    const rows = [
      ['incubation_key', 'String (Required)'],
      ['access_key',     'String (Required)'],
      ...dataKeys.map(({ key, type }) => [`data_${key}`, type]),
      ...criteriaKeys.map((key) => [`data_${key}`, 'Boolean']),
    ];

    const indent = (n) => '  '.repeat(n);
    const lines = [];
    lines.push('{');
    if (rows.length === 2) {
      lines.push(`${indent(1)}// Add data_<field_name> entries for each data field or criterion`);
    }
    rows.forEach(([k, v], idx) => {
      const isLast = idx === rows.length - 1;
      lines.push(`${indent(1)}"${k}": ${v}${isLast ? '' : ','}`);
    });
    lines.push('}');
    return lines.join('\n');
  }, [incubation]);

  // Advanced flat example: rename a field on an existing participant
  const sampleFlatAdvancedDisplay = useMemo(() => {
    const TypeToken = ({ text }) => (
      <span style={{ color: 'maroon' }}>{text}</span>
    );

    const firstDataField = (incubation?.data_fields || []).find(f => f && f.name);
    const exampleKey = firstDataField ? firstDataField.name : 'name';
    const exampleType = firstDataField ? mapFieldTypeToText(firstDataField.type) : 'String';

    const lines = [];
    lines.push('{');
    lines.push(
      <React.Fragment key="adv-incubation_key">
        {'  "incubation_key": '}<TypeToken text="String" />{' (Required),'}
      </React.Fragment>
    );
    lines.push(
      <React.Fragment key="adv-access_key">
        {'  "access_key": '}<TypeToken text="String" />{' (Required),'}
      </React.Fragment>
    );
    lines.push(
      <React.Fragment key="adv-query">
        {`  "query_${exampleKey}": `}<TypeToken text={exampleType} />{' (old value — used to find existing participant),'}
      </React.Fragment>
    );
    lines.push(
      <React.Fragment key="adv-data">
        {`  "data_${exampleKey}": `}<TypeToken text={exampleType} />{' (new value to update)'}
      </React.Fragment>
    );
    lines.push('}');

    return (
      <code>
        {lines.map((line, idx) => (
          <React.Fragment key={`fa-${idx}`}>
            {line}
            {'\n'}
          </React.Fragment>
        ))}
      </code>
    );
  }, [incubation]);

  const sampleFlatAdvancedCopy = useMemo(() => {
    const firstDataField = (incubation?.data_fields || []).find(f => f && f.name);
    const exampleKey = firstDataField ? firstDataField.name : 'name';
    const exampleType = firstDataField ? mapFieldTypeToText(firstDataField.type) : 'String';

    const lines = [
      '{',
      `  "incubation_key": String (Required),`,
      `  "access_key": String (Required),`,
      `  "query_${exampleKey}": ${exampleType} (old value — used to find existing participant),`,
      `  "data_${exampleKey}": ${exampleType} (new value to update)`,
      '}',
    ];
    return lines.join('\n');
  }, [incubation]);

  // Fetch webhook access key on mount
  useEffect(() => {
    const fetchAccessKey = async () => {
      const incubationId = incubation?.id;
      if (!incubationId || typeof incubationId !== 'string' || incubationId.trim().length === 0) {
        return; // No incubation context; leave accessKey empty
      }

      try {
        const response = await getWebhookJoin({ incubation_id: incubationId });
        // Endpoint returns HTTP 200 always; check success flag
        if (response?.data?.success && response?.data?.webhook_join?.access_key) {
          setAccessKey(response.data.webhook_join.access_key);
        } else {
          const errMsg = response?.data?.error || 'Failed to fetch webhook key';
          showError(errMsg);
        }
      } catch (err) {
        const errMsg = err?.response?.data?.error || err.message || 'Failed to fetch webhook key';
        showError(errMsg);
      }
    };

    fetchAccessKey();
    // do not log access keys
  }, [incubation]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleTestWebhook = async () => {
    setIsTestingWebhook(true);
    const loadingToastId = showLoading('Testing webhook...');

    try {
      const testData = {
        incubation_key: incubation?.shareable_id, 
        access_key: accessKey, 
        query: { name: "dart" },
        data: { name: "Kenny", parent_first_name: "Jane", parent_last_name: "Doe", "age_>_10": true }
      };

      const response = await testWebhook(testData);
      showSuccess(`Webhook test successful! Response: ${response.data.message || 'OK'}`);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      showError(`Webhook test failed: ${errorMessage}`);
    } finally {
      setIsTestingWebhook(false);
      // Dismiss the loading toast
      if (loadingToastId) {
        dismiss(loadingToastId);
      }
    }
  };

  return (
    <div className="webhook-instructions-screen">
      <div className="webhook-header-container">
        <button className="webhook-back-button" onClick={handleBackClick}>
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </button>
        <div className="webhook-header">
          <h1 className="webhook-title">Webhook Instructions</h1>
        </div>
      </div>
      
      <div className="webhook-content">
        <Card className="webhook-instructions-card">
          <div className="webhook-format-tabs">
            <button
              type="button"
              className={`webhook-format-tab ${bodyFormat === 'standard' ? 'active' : ''}`}
              onClick={() => setBodyFormat('standard')}
            >
              Standard (JSON)
            </button>
            <button
              type="button"
              className={`webhook-format-tab ${bodyFormat === 'zapier' ? 'active' : ''}`}
              onClick={() => setBodyFormat('zapier')}
            >
              Zapier (Flat)
            </button>
          </div>

          <div className="webhook-section">
            <h2 className="webhook-section-title">API Endpoint</h2>
            <div className="webhook-endpoint">
              <code className="endpoint-url">POST {fullEndpoint}</code>
              <button className="copy-button" onClick={() => navigator.clipboard.writeText(fullEndpoint)}>
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>

          <div className="webhook-section">
            <h2 className="webhook-section-title">Incubation Key</h2>
            <div className="webhook-api-key">
              <code className="api-key">{incubation?.shareable_id || 'inc_1234567890abcdef1234567890abcdef'}</code>
              <button className="copy-button" onClick={() => navigator.clipboard.writeText(incubation?.shareable_id || 'inc_1234567890abcdef1234567890abcdef')}>
                <i className="fas fa-copy"></i>
              </button>
            </div>
            <p className="webhook-note">Include this incubation key in your request body to identify the specific incubation.</p>
          </div>

          <div className="webhook-section">
            <h2 className="webhook-section-title">Access Key</h2>
            <div className="webhook-api-key">
              <code className="api-key">{showAccessKey ? (accessKey || 'Loading...') : (accessKey ? '*'.repeat(accessKey.length) : 'Loading...')}</code>
              <button className="copy-button" onClick={() => navigator.clipboard.writeText(accessKey || '')}>
                <i className="fas fa-copy"></i>
              </button>
              <button className="copy-button" onClick={() => setShowAccessKey(v => !v)} aria-label={showAccessKey ? 'Hide access key' : 'Show access key'}>
                <i className={showAccessKey ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </button>
            </div>
          </div>

          <div className="webhook-section">
            <h2 className="webhook-section-title">Sample Request Body</h2>
            {bodyFormat === 'zapier' && (
              <p className="webhook-note">
                Each field is a top-level key prefixed with <code>data_</code>. Sending a <code>data_X</code> value also makes it the lookup value for that field, so one mapping creates-or-updates the participant. Empty values are treated as "not provided" and skipped.
              </p>
            )}
            <div className="webhook-sample-code">
              <pre className="code-block">
{bodyFormat === 'zapier' ? sampleFlatBodyDisplay : sampleRequestBodyDisplay}
              </pre>
              <button
                className="copy-button"
                onClick={() => navigator.clipboard.writeText(
                  bodyFormat === 'zapier' ? sampleFlatBodyCopy : sampleRequestBodyCopy
                )}
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>

          {bodyFormat === 'zapier' && (
            <div className="webhook-section">
              <h2 className="webhook-section-title">Advanced: Rename a Field on an Existing Participant</h2>
              <p className="webhook-note">
                To rename a field, send the <em>old</em> value with a <code>query_</code> prefix and the <em>new</em> value with a <code>data_</code> prefix. The old value is used to find the participant; the new value replaces it.
              </p>
              <div className="webhook-sample-code">
                <pre className="code-block">
{sampleFlatAdvancedDisplay}
                </pre>
                <button
                  className="copy-button"
                  onClick={() => navigator.clipboard.writeText(sampleFlatAdvancedCopy)}
                >
                  <i className="fas fa-copy"></i>
                </button>
              </div>
            </div>
          )}

          <div className="webhook-section">
            <h2 className="webhook-section-title">Response Format</h2>
            <div className="webhook-sample-code">
              <pre className="code-block">
{sampleResponseBody}
              </pre>
              <button className="copy-button" onClick={() => navigator.clipboard.writeText(sampleResponseBody)}>
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>

          {/* <div className="webhook-section">
            <h2 className="webhook-section-title">Test Webhook</h2>
            <p className="webhook-note">Click the button below to send a test webhook request with sample data to verify your integration.</p>
            <div className="webhook-test-container">
              <Button
                onClick={handleTestWebhook}
                loading={isTestingWebhook}
                disabled={isTestingWebhook}
                variant="primary"
                size="medium"
                className="test-webhook-button"
              >
                <i className="fas fa-paper-plane"></i>
                {isTestingWebhook ? 'Testing...' : 'Test Webhook'}
              </Button>
            </div>
          </div> */}

        </Card>
      </div>
    </div>
  );
};

export default WebhookInstructionsScreen;
